import WhatsappCallsAPI from 'dashboard/api/channel/whatsapp/whatsappCallsAPI';
import { VOICE_CALL_OUTBOUND_INIT_STATUS } from 'dashboard/components-next/message/constants';
import Cookies from 'js-cookie';
import { readonly, ref } from 'vue';

export const WHATSAPP_CALL_TRANSPORT = {
  DATA_CHANNEL: 'data_channel',
  MEDIA_STREAM: 'media_stream',
};

// Module-level state lets the cable handlers and unload listeners reach the
// live PeerConnection without prop-drilling refs through every composable.
let pc = null;
let localStream = null;
let remoteStream = null;
let remoteAudioEl = null;
let mediaRecorder = null;
let recorderChunks = [];
let audioContext = null;
let activeCallId = null;
// voice_call.outbound_connected (the sole source of the outbound SDP answer) is
// broadcast account-wide and can arrive before the /initiate response sets
// activeCallId in this tab. Until we know our own call id we can't tell our
// answer from a concurrent agent's, so the cable handler buffers them here keyed
// by call id (a single slot would let a concurrent agent's event clobber ours)
// and initiateOutboundCall flushes the matching one by id.
const pendingOutboundAnswers = new Map();
// Module-scoped so multiple composable callers (header button + contact-panel
// button) share the same lock. A per-instance ref let two parallel callers
// both pass the guard and tear down each other's WebRTC state in cleanup().
// ref() so consumers can reactively gate buttons on it (the composable
// re-exports this as `isInitiating`).
const isInitiatingOutbound = ref(false);
const isInitiatingOutboundReadonly = readonly(isInitiatingOutbound);
// Inbound calls record from the moment the agent clicks accept (their click =
// pickup). Outbound calls must wait — Meta's `connect` webhook (which lands
// during ringing) negotiates remote tracks ~20s before the contact actually
// answers, and we don't want pre-pickup audio in the recording. This flag is
// flipped to true by armOutboundRecorder() when the ACCEPTED status arrives.
let recorderArmed = false;
let bridgePc = null;
let bridgeAudioContext = null;
let bridgeMicStream = null;
let bridgeCallId = null;
let sessionFinalizationPromise = null;
let setupDataChannelBridge = null;
let watchWhatsappMediaPath = null;

// Reactive view of the media path for Vue consumers (the call card): dispatch
// events stay for non-Vue listeners, these refs are what the UI should bind to
// so it never shows "connected" before ICE is actually up. Declared alongside
// the other module state so the bridge teardown can reset them.
export const whatsappMediaReady = ref(false);
export const whatsappReconnecting = ref(false);
// Latest network verdict for the call card (null before the first sample).
export const whatsappCallQuality = ref(null);

// Sampling timer for the network verdict (see startWhatsappQualitySampling).
let qualityTimer = null;

export const stopWhatsappQualitySampling = () => {
  if (qualityTimer) {
    window.clearInterval(qualityTimer);
    qualityTimer = null;
  }
  whatsappCallQuality.value = null;
};

const ensureRemoteAudioElement = () => {
  if (remoteAudioEl) return remoteAudioEl;
  remoteAudioEl = document.createElement('audio');
  remoteAudioEl.id = 'whatsapp-call-remote-audio';
  remoteAudioEl.autoplay = true;
  remoteAudioEl.playsInline = true;
  remoteAudioEl.style.display = 'none';
  document.body.appendChild(remoteAudioEl);
  return remoteAudioEl;
};

const playRemoteStream = stream => {
  const el = ensureRemoteAudioElement();
  el.srcObject = stream;
  el.play().catch(err => {
    // eslint-disable-next-line no-console
    console.warn('[WhatsApp Call] remote audio play() failed:', err);
  });
};

// A 1s timeslice keeps recent recording data available when a remote hangup
// races session cleanup.
const RECORDING_TIMESLICE_MS = 1000;
const ICE_GATHER_TIMEOUT_MS = 10000;

const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

// Outbound Meta calls and the WhatsApp Go browser bridge have no
// backend-supplied ICE servers. Without STUN the browser only advertises host
// candidates, which cannot reach Meta or a remote gateway through NAT.
const DEFAULT_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const waitForIceGatheringComplete = peer =>
  new Promise(resolve => {
    if (peer.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ICE_GATHER_TIMEOUT_MS);
    peer.addEventListener('icegatheringstatechange', () => {
      if (peer.iceGatheringState === 'complete') {
        clearTimeout(timer);
        resolve();
      }
    });
  });

// Autoplay policies create an AudioContext suspended until the document has
// user activation, and resume() only settles once it does. Awaiting it unbounded
// can hang call setup forever (the call button spins with no error), so give it
// a bounded wait and let the first playback frame retry it.
const resumeAudioContext = async (ctx, timeoutMs = 1500) => {
  if (!ctx || ctx.state === 'running') return;
  await Promise.race([
    ctx.resume().catch(() => {}),
    new Promise(resolve => {
      setTimeout(resolve, timeoutMs);
    }),
  ]);
};

const float32ToInt16LE = pcm => {
  const view = new DataView(new ArrayBuffer(pcm.length * 2));
  for (let index = 0; index < pcm.length; index += 1) {
    let sample = pcm[index];
    if (Number.isNaN(sample)) sample = 0;
    else if (sample > 1) sample = 1;
    else if (sample < -1) sample = -1;
    view.setInt16(
      index * 2,
      sample < 0 ? Math.round(sample * 32768) : Math.round(sample * 32767),
      true
    );
  }
  return view.buffer;
};

const int16LEToFloat32 = buffer => {
  const view = new DataView(buffer);
  const output = new Float32Array(Math.floor(buffer.byteLength / 2));
  for (let index = 0; index < output.length; index += 1) {
    output[index] = view.getInt16(index * 2, true) / 32768;
  }
  return output;
};

const setupRecorder = () => {
  if (!localStream || !remoteStream || mediaRecorder) return;
  // createMediaStreamSource on a stream with no audio tracks wires up to
  // nothing — the recorded mix would be silence. Wait until ontrack fires.
  if (remoteStream.getAudioTracks().length === 0) return;

  audioContext = new AudioContext({ sampleRate: 48000 });
  // AudioContext starts suspended under most autoplay policies; without
  // resume() the destination stream produces silence.
  audioContext.resume().catch(() => {});

  const destination = audioContext.createMediaStreamDestination();
  audioContext.createMediaStreamSource(localStream).connect(destination);
  audioContext.createMediaStreamSource(remoteStream).connect(destination);

  const mimeType = RECORDER_MIME_CANDIDATES.find(t =>
    MediaRecorder.isTypeSupported(t)
  );
  if (!mimeType) return;

  recorderChunks = [];
  mediaRecorder = new MediaRecorder(destination.stream, { mimeType });
  mediaRecorder.ondataavailable = event => {
    if (event.data && event.data.size > 0) recorderChunks.push(event.data);
  };
  mediaRecorder.start(RECORDING_TIMESLICE_MS);
};

const closeDataChannelBridgeResources = () => {
  if (!bridgePc && !bridgeAudioContext && !bridgeMicStream && !bridgeCallId) {
    return;
  }

  const mic = bridgeMicStream;
  const playbackStream = remoteStream;

  try {
    bridgePc?.close();
  } catch (_) {
    /* noop */
  }
  if (bridgeAudioContext && bridgeAudioContext.state !== 'closed') {
    bridgeAudioContext.close().catch(() => {});
  }
  if (mic) mic.getTracks().forEach(track => track.stop());
  if (playbackStream && playbackStream !== mic) {
    playbackStream.getTracks().forEach(track => track.stop());
  }
  if (localStream === mic) localStream = null;
  if (remoteStream === playbackStream) remoteStream = null;

  bridgePc = null;
  bridgeAudioContext = null;
  bridgeMicStream = null;
  bridgeCallId = null;

  // The bridge is gone, so there is no media path until a new one connects:
  // drop the media state the call card binds to.
  whatsappMediaReady.value = false;
  whatsappReconnecting.value = false;
  stopWhatsappQualitySampling();
};

const cleanup = () => {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    try {
      mediaRecorder.stop();
    } catch (_) {
      /* noop */
    }
  }
  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close().catch(() => {});
  }
  closeDataChannelBridgeResources();
  if (localStream) localStream.getTracks().forEach(t => t.stop());
  if (remoteStream) remoteStream.getTracks().forEach(t => t.stop());
  if (pc) pc.close();
  if (remoteAudioEl) remoteAudioEl.srcObject = null;

  pc = null;
  localStream = null;
  remoteStream = null;
  mediaRecorder = null;
  recorderChunks = [];
  audioContext = null;
  activeCallId = null;
  pendingOutboundAnswers.clear();
  recorderArmed = false;
};

const buildPeerConnection = iceServers => {
  const config = iceServers && iceServers.length ? { iceServers } : {};
  pc = new RTCPeerConnection(config);
  remoteStream = new MediaStream();
  pc.ontrack = event => {
    // Reuse the same MediaStream object — the recorder's audioContext source
    // taps it once, so reassigning would orphan the recorder.
    const tracks =
      event.streams && event.streams[0]
        ? event.streams[0].getTracks()
        : [event.track];
    tracks.forEach(track => {
      if (!remoteStream.getTracks().includes(track))
        remoteStream.addTrack(track);
    });
    playRemoteStream(remoteStream);
    // Only arm the recorder when the call is actually accepted. For outbound
    // this is the ACCEPTED status webhook; for inbound this is the agent's
    // own click (acceptIncomingCall flips recorderArmed before returning).
    if (recorderArmed) setupRecorder();
  };
  return pc;
};

const stopRecorderAndUpload = async callId => {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    await new Promise(resolve => {
      mediaRecorder.addEventListener('stop', resolve, { once: true });
      try {
        mediaRecorder.stop();
      } catch (_) {
        resolve();
      }
    });
  }
  if (!recorderChunks.length || !callId) return;

  const blob = new Blob(recorderChunks, { type: recorderChunks[0].type });
  // Best-effort — the controller's idempotency guard handles a retry.
  try {
    await WhatsappCallsAPI.uploadRecording(callId, blob);
  } catch (_) {
    /* noop */
  }
};

const finalizeWhatsappSession = callId => {
  if (sessionFinalizationPromise) return sessionFinalizationPromise;

  sessionFinalizationPromise = (async () => {
    try {
      await stopRecorderAndUpload(callId);
    } finally {
      cleanup();
    }
  })().finally(() => {
    sessionFinalizationPromise = null;
  });
  return sessionFinalizationPromise;
};

// devise-token-auth requires access-token / client / uid headers on every
// request — navigator.sendBeacon can't set custom headers, so we rehydrate
// the auth payload from the cw_d_session_info cookie that the dashboard sets
// at login. Used by the page-close terminate path below.
const getDeviseAuthHeaders = () => {
  try {
    const raw = Cookies.get('cw_d_session_info');
    if (!raw) return null;
    const session = JSON.parse(raw);
    return {
      'access-token': session['access-token'] || '',
      client: session.client || '',
      uid: session.uid || '',
      expiry: session.expiry || '',
      'token-type': session['token-type'] || 'Bearer',
    };
  } catch (_) {
    return null;
  }
};

const beaconTerminate = callId => {
  if (!callId) return;
  const accountId = window.location.pathname.split('/')[3];
  if (!accountId) return;
  const headers = getDeviseAuthHeaders();
  if (!headers) return;
  const url = `/api/v1/accounts/${accountId}/whatsapp_calls/${callId}/terminate`;
  // fetch+keepalive (instead of navigator.sendBeacon) so we can attach auth
  // headers — without them devise-token-auth 401s and the call stays open on
  // Meta until its carrier-side timeout (~60s).
  try {
    fetch(url, {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: '{}',
    }).catch(() => {});
  } catch (_) {
    /* noop */
  }
};

export const hasActiveWhatsappCall = () => !!(activeCallId || pc);

export const isLocalWhatsappCall = callId =>
  !!callId && activeCallId != null && callId === activeCallId;

export function useWhatsappCallSession() {
  const prepareInboundAnswer = async (sdpOffer, iceServers) => {
    cleanup();
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    buildPeerConnection(iceServers);
    localStream.getTracks().forEach(t => pc.addTrack(t, localStream));
    await pc.setRemoteDescription({ type: 'offer', sdp: sdpOffer });
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await waitForIceGatheringComplete(pc);
    return pc.localDescription.sdp;
  };

  const prepareOutboundOffer = async () => {
    cleanup();
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    buildPeerConnection(DEFAULT_ICE_SERVERS);
    localStream.getTracks().forEach(t => pc.addTrack(t, localStream));
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitForIceGatheringComplete(pc);
    return pc.localDescription.sdp;
  };

  // Mic-only counterpart to prepareOutboundOffer for the gateway bridge. The
  // bridge builds its own 16 kHz audio graph once the call exists, so the only
  // part that has to happen inside the click's user activation is the
  // permission-gated capture — browsers only show that prompt for a
  // gesture-initiated call, and the bridge is reached several awaits (and a
  // first-call worklet fetch) later.
  const prepareOutboundMic = async () => {
    cleanup();
    localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    return localStream;
  };

  const acceptIncomingCall = async ({
    callId,
    sdpOffer,
    iceServers,
    transport,
  }) => {
    // The store may not have sdpOffer yet (the cable broadcast can race the
    // click). Fall back to GET /whatsapp_calls/:id which exposes it.
    let offer = sdpOffer;
    let ice = iceServers;
    let callTransport = transport;
    if ((!offer || !callTransport) && callId) {
      try {
        const fresh = await WhatsappCallsAPI.show(callId);
        offer = fresh?.sdp_offer || fresh?.sdpOffer;
        ice = ice || fresh?.ice_servers || fresh?.iceServers;
        callTransport = callTransport || fresh?.transport;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          '[WhatsApp Call] failed to fetch call data for accept:',
          e
        );
      }
    }
    if (callTransport === WHATSAPP_CALL_TRANSPORT.DATA_CHANNEL) {
      activeCallId = callId;
      try {
        // Match the working standalone client: claim/accept the WhatsApp leg,
        // then open the browser DataChannel bridge for its audio.
        await WhatsappCallsAPI.accept(callId);
        recorderArmed = true;
        await setupDataChannelBridge(callId, ice);
        setupRecorder();
        return;
      } catch (error) {
        await WhatsappCallsAPI.terminate(callId).catch(() => {});
        cleanup();
        throw error;
      }
    }
    if (!offer) {
      throw new Error('Missing sdp_offer for accept — call may have ended.');
    }

    // Release the mic + peer connection if anything between here and the accept
    // round-trip fails — otherwise a rejected accept leaves the mic live and
    // activeCallId set. Mirrors rejectIncomingCall's self-cleanup; rethrow so
    // the caller can surface the failure and skip marking the call active.
    try {
      const sdpAnswer = await prepareInboundAnswer(offer, ice);
      activeCallId = callId;
      // Inbound: agent's click is the pickup. Arm the recorder before the API
      // round-trip so when ontrack fires (triggered by setRemoteDescription
      // back in prepareInboundAnswer) the recorder is already authorized.
      recorderArmed = true;
      setupRecorder();
      await WhatsappCallsAPI.accept(callId, sdpAnswer);
    } catch (e) {
      cleanup();
      throw e;
    }
  };

  const rejectIncomingCall = async callId => {
    try {
      await WhatsappCallsAPI.reject(callId);
    } finally {
      cleanup();
    }
  };

  // Pre-call media path check.
  //
  // `iceTransportPolicy: 'relay'` means the browser only gathers TURN candidates,
  // so this answers "can we reach a relay?" before we bridge audio - instead of
  // dialling, reporting connected, and leaving the agent with silence.
  const preflightRelayPath = async (iceServers, timeoutMs = 3000) => {
    if (typeof RTCPeerConnection === 'undefined')
      return { ok: false, reason: 'webrtc-unavailable' };
    const peer = new RTCPeerConnection({
      iceServers: iceServers || [],
      iceTransportPolicy: 'relay',
    });
    try {
      peer.createDataChannel('preflight');
      const relay = await new Promise(resolve => {
        let settled = false;
        let timer = null;
        const finish = found => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(found);
        };
        timer = setTimeout(() => finish(false), timeoutMs);
        peer.onicecandidate = event => {
          if (event.candidate?.type === 'relay') finish(true);
        };
        peer.onicegatheringstatechange = () => {
          if (
            peer.iceGatheringState === 'complete' &&
            !peer.localDescription?.sdp?.includes('typ relay')
          )
            finish(false);
        };
        peer
          .createOffer()
          .then(offer => peer.setLocalDescription(offer))
          .catch(() => finish(false));
      });
      return relay ? { ok: true } : { ok: false, reason: 'no-relay-candidate' };
    } catch {
      return { ok: false, reason: 'error' };
    } finally {
      peer.close();
    }
  };

  const initiateOutboundCall = async (conversationId, inboxProvider = null) => {
    // Module-scoped lock + active-session guard so a second click — from the
    // same composable instance OR a different one (header vs contact panel)
    // OR while a call is already live — can't tear down the in-flight setup
    // via prepareOutboundOffer's cleanup().
    if (isInitiatingOutbound.value)
      return { status: VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED };
    if (hasActiveWhatsappCall())
      return { status: VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED };
    isInitiatingOutbound.value = true;
    try {
      const usesDataChannel = inboxProvider === 'whatsapp_go';
      const sdpOffer = usesDataChannel ? null : await prepareOutboundOffer();
      // Capture the mic while the click is still the active user gesture. The
      // gateway bridge is only reached after the /initiate round-trip, so
      // asking for it there ran past the browser's activation window and the
      // prompt never appeared.
      const mic = usesDataChannel ? await prepareOutboundMic() : null;
      const response = await WhatsappCallsAPI.initiate(
        conversationId,
        sdpOffer
      );
      if (response?.id) {
        if (response.transport === WHATSAPP_CALL_TRANSPORT.DATA_CHANNEL) {
          // If the caller did not know the inbox subtype, release the temporary
          // Meta-style PeerConnection before switching to the gateway bridge.
          // A pre-captured mic (the known-whatsapp_go path) is left untouched.
          if (pc) cleanup();
          activeCallId = response.id;
          const iceServers = response.ice_servers || response.iceServers;
          // Relay reachability is a diagnostic, never a gate. The bridge
          // gathers its own candidates and reports the real ICE state to the
          // call card, whereas terminating on this probe killed the call
          // before the mic was ever requested (and the probe itself is
          // time-sensitive on the agent's network, so it flapped).
          preflightRelayPath(iceServers)
            .then(path => {
              if (path.ok) return;
              // eslint-disable-next-line no-console
              console.warn(
                `[WhatsApp Call] relay preflight failed: ${path.reason}`
              );
            })
            .catch(() => {});
          try {
            await setupDataChannelBridge(response.id, iceServers, mic);
          } catch (error) {
            await WhatsappCallsAPI.terminate(response.id).catch(() => {});
            throw error;
          }
          return response;
        }

        // The provider turned out not to be on the gateway bridge after all, so
        // the stream captured up front feeds nothing — release it instead of
        // leaving the mic live for the rest of the call.
        if (mic) cleanup();
        activeCallId = response.id;
        // A connect webhook that raced ahead of this response was buffered;
        // apply our own by id now that we know it, then drop every buffered
        // answer (concurrent agents' calls aren't ours to apply).
        const buffered = pendingOutboundAnswers.get(activeCallId);
        pendingOutboundAnswers.clear();
        if (buffered) {
          await pc.setRemoteDescription({
            type: 'answer',
            sdp: buffered,
          });
        }
        return response;
      }
      // No call id back: this is the permission-request branch. The mic +
      // PeerConnection allocated by prepareOutboundOffer aren't useful until
      // the contact opts in and the agent retries — release them.
      cleanup();
      return response;
    } catch (e) {
      cleanup();
      // BE returns 422 when the contact hasn't opted in (permission template
      // sent or already pending). Surface it to the caller as a normal
      // response shape so it can render the banner instead of an error toast.
      const data = e?.response?.data;
      if (
        data?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_REQUESTED ||
        data?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_PENDING
      ) {
        return { status: data.status };
      }
      throw e;
    } finally {
      isInitiatingOutbound.value = false;
    }
  };

  // callIdOverride is the call.id from the dashboard's calls store. Module
  // `activeCallId` may be null after a prior accept attempt's cleanup() — but
  // the call still exists on Meta and must still be terminated. Falling back
  // to the override means hangup is robust to a wiped local session.
  const endActiveCall = async (callIdOverride = null) => {
    const callId = activeCallId || callIdOverride;
    if (!callId) {
      cleanup();
      return;
    }
    try {
      // Terminate on Meta first so the contact is disconnected immediately. The
      // recording upload below can be slow on long calls or poor networks, and
      // the peer connection / mic must not stay live for the contact during it.
      await WhatsappCallsAPI.terminate(callId).catch(() => {});
      await finalizeWhatsappSession(callId);
    } catch (error) {
      cleanup();
      throw error;
    }
  };

  return {
    isInitiating: isInitiatingOutboundReadonly,
    prepareInboundAnswer,
    prepareOutboundOffer,
    acceptIncomingCall,
    rejectIncomingCall,
    initiateOutboundCall,
    endActiveCall,
  };
}

// Cable handlers fire outside any composable instance, so the shared session
// surface is exposed as module-level functions for them.

setupDataChannelBridge = async (
  callId,
  iceServers = null,
  micStream = null
) => {
  if (!callId) return;
  if (bridgePc && bridgeCallId === callId) return;

  // Tear down any prior bridge (e.g. from a re-join). The caller-supplied mic
  // is captured in the click's user activation, so hand it over before this
  // point and it is never stopped here.
  closeDataChannelBridgeResources();

  try {
    let bridgeIceServers = iceServers;
    if (!bridgeIceServers?.length) {
      const fresh = await WhatsappCallsAPI.show(callId);
      bridgeIceServers = fresh?.ice_servers || fresh?.iceServers;
    }
    const peer = new RTCPeerConnection({
      iceServers: bridgeIceServers?.length
        ? bridgeIceServers
        : DEFAULT_ICE_SERVERS,
    });
    bridgePc = peer;
    bridgeCallId = callId;
    const dc = peer.createDataChannel('pcm', { ordered: true });
    dc.binaryType = 'arraybuffer';

    const ctx = new AudioContext({ sampleRate: 16000 });
    bridgeAudioContext = ctx;
    await ctx.audioWorklet.addModule('/worklets/capture-processor.js');
    await ctx.audioWorklet.addModule('/worklets/playback-processor.js');
    await resumeAudioContext(ctx);

    // Reuse the stream captured in the click's user activation when we have
    // one (outbound); inbound and the transport-mismatch fallback capture here.
    const mic =
      micStream ||
      (await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      }));
    bridgeMicStream = mic;
    localStream = mic;
    const micSource = ctx.createMediaStreamSource(mic);
    const captureNode = new AudioWorkletNode(ctx, 'capture-processor');
    captureNode.port.onmessage = e => {
      if (dc.readyState !== 'open') return;
      dc.send(float32ToInt16LE(e.data));
    };
    micSource.connect(captureNode);

    const playbackNode = new AudioWorkletNode(ctx, 'playback-processor');
    const playbackDestination = ctx.createMediaStreamDestination();
    playbackNode.connect(playbackDestination);
    remoteStream = playbackDestination.stream;
    playRemoteStream(remoteStream);
    dc.onmessage = e => {
      // A context that stayed suspended through setup (no user activation yet)
      // would render the whole call silent; retry once frames start arriving.
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      playbackNode.port.postMessage(int16LEToFloat32(e.data));
    };

    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    await new Promise(resolve => {
      if (peer.iceGatheringState === 'complete') {
        resolve();
        return;
      }
      const timer = setTimeout(resolve, 5000);
      peer.addEventListener('icegatheringstatechange', () => {
        if (peer.iceGatheringState === 'complete') {
          clearTimeout(timer);
          resolve();
        }
      });
    });

    const result = await WhatsappCallsAPI.startWebrtc(
      callId,
      peer.localDescription.sdp
    );
    const sdpAnswer = result?.sdp_answer || result?.sdpAnswer;
    if (!sdpAnswer) throw new Error('WhatsApp Go returned no WebRTC answer.');

    await peer.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
    // The gateway bridge owns its own peer connection, so it has to arm the
    // media watcher as well. Only the Meta-style path (applyOutboundAnswer)
    // armed it before, which left the card reading "Connecting..." for the
    // whole call even while audio was already flowing.
    //
    // No quality sampling here: this call's audio travels as PCM over the data
    // channel, so getStats() sees no inbound RTP and would report a bogus
    // "your network is struggling" verdict.
    watchWhatsappMediaPath(peer, { sampleQuality: false });
    if (recorderArmed) setupRecorder();
  } catch (e) {
    closeDataChannelBridgeResources();
    throw e;
  }
};

export { setupDataChannelBridge };

export const teardownDataChannelBridge = () => {
  closeDataChannelBridgeResources();
};

// Ringback for the agent while the contact's phone is ringing.
//
// Meta negotiates the remote tracks during ringing (the `connect` webhook), so
// "answer applied" is exactly the ringing window for an outbound call: start the
// tone there and stop it at real pickup (`armOutboundRecorder`). RFC 3960 §3.2
// has the caller generate ringback while it has no early media to play.
let ringbackContext = null;
let ringbackTimer = null;

export const startWhatsappRingback = () => {
  if (ringbackContext) return;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return;
  try {
    ringbackContext = new Ctor();
    const gain = ringbackContext.createGain();
    gain.gain.value = 0.06;
    gain.connect(ringbackContext.destination);
    const burst = () => {
      const osc = ringbackContext.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 425;
      osc.connect(gain);
      osc.start();
      osc.stop(ringbackContext.currentTime + 1);
    };
    burst();
    ringbackTimer = window.setInterval(burst, 3000);
  } catch {
    ringbackContext = null;
  }
};

export const stopWhatsappRingback = () => {
  if (ringbackTimer) {
    window.clearInterval(ringbackTimer);
    ringbackTimer = null;
  }
  if (ringbackContext) {
    try {
      ringbackContext.close();
    } catch {
      /* nothing to release */
    }
    ringbackContext = null;
  }
};

// Media path of the WhatsApp leg.
//
// "Media ready" is an ICE fact, not a webhook: only once the peer connection is
// connected/completed is there any point telling the UI the call is up (before
// that the tracks are negotiated but silent). And when ICE drops, restart it
// instead of leaving an open call with no audio - `restartIce()` is the
// documented recovery - while telling the UI to show "Reconnecting...".

// Call quality, from the peer connection's own statistics.
//
// inbound-rtp describes what we receive (the far end's uplink and our
// downlink); remote-inbound-rtp describes what the far end receives from us
// (our uplink). Comparing them, plus the candidate pair, is how we tell the
// agent's network from ours - see ruut-voice/plans/call-quality-plan.md.
export const WHATSAPP_QUALITY = {
  OK: 'ok',
  DEGRADED: 'degraded',
  LOCAL: 'local-network',
  REMOTE: 'remote-network',
  RELAY: 'relay',
  UNKNOWN: 'unknown',
};

const JITTER_LIMIT = 0.03;
const LOSS_LIMIT = 0.02;

export const assessWhatsappQuality = report => {
  const stats = [...(report?.values?.() || [])];
  const audio = entry =>
    entry.type === 'inbound-rtp' &&
    (entry.kind === 'audio' || entry.mediaType === 'audio');
  const remoteAudio = entry =>
    entry.type === 'remote-inbound-rtp' &&
    (entry.kind === 'audio' || entry.mediaType === 'audio');
  const inbound = stats.find(audio);
  const remote = stats.find(remoteAudio);
  const pair = stats.find(
    entry =>
      entry.type === 'candidate-pair' &&
      (entry.state === 'succeeded' || entry.nominated)
  );
  const relay =
    !!pair &&
    stats.some(
      entry =>
        entry.type === 'local-candidate' &&
        entry.id === pair.localCandidateId &&
        entry.candidateType === 'relay'
    );

  if (!inbound) return { verdict: WHATSAPP_QUALITY.UNKNOWN };

  const received = inbound.packetsReceived || 0;
  const lost = inbound.packetsLost || 0;
  const localLoss = lost / Math.max(received + lost, 1);
  const localBad =
    received === 0 ||
    (inbound.jitter || 0) > JITTER_LIMIT ||
    localLoss > LOSS_LIMIT;

  const remoteLost = remote?.packetsLost || 0;
  const remoteLoss = remote
    ? remoteLost / Math.max(remoteLost + received, 1)
    : 0;
  const remoteBad = remote
    ? (remote.jitter || 0) > JITTER_LIMIT || remoteLoss > LOSS_LIMIT
    : false;

  let verdict = WHATSAPP_QUALITY.OK;
  if (localBad && remoteBad) verdict = WHATSAPP_QUALITY.DEGRADED;
  else if (localBad) verdict = WHATSAPP_QUALITY.LOCAL;
  else if (remoteBad) verdict = WHATSAPP_QUALITY.REMOTE;
  else if (relay) verdict = WHATSAPP_QUALITY.RELAY;

  return {
    verdict,
    inbound: {
      jitter: inbound.jitter || 0,
      packetsLost: lost,
      concealedSamples: inbound.concealedSamples || 0,
    },
    remote: remote
      ? {
          jitter: remote.jitter || 0,
          packetsLost: remoteLost,
          roundTripTime: remote.roundTripTime || 0,
        }
      : null,
    relay,
  };
};

// Sample the call's statistics every few seconds so the card can say whether a
// rough call is the agent's network or the far end's.
const startWhatsappQualitySampling = peer => {
  if (qualityTimer) return;
  const sample = async () => {
    try {
      whatsappCallQuality.value = assessWhatsappQuality(await peer.getStats());
    } catch {
      /* statistics are best-effort */
    }
  };
  sample();
  qualityTimer = window.setInterval(sample, 5000);
};

watchWhatsappMediaPath = (peer, { sampleQuality = true } = {}) => {
  const onIceState = () => {
    const { iceConnectionState: state } = peer;
    if (state === 'connected' || state === 'completed') {
      whatsappMediaReady.value = true;
      whatsappReconnecting.value = false;
      if (sampleQuality) startWhatsappQualitySampling(peer);
      window.dispatchEvent(new CustomEvent('whatsapp:call-media-ready'));
      return;
    }
    if (state === 'failed') {
      try {
        peer.restartIce();
      } catch {
        /* no ICE restart support: the notice below is all we can do */
      }
    }
    if (state === 'disconnected' || state === 'failed') {
      whatsappMediaReady.value = false;
      whatsappReconnecting.value = true;
      window.dispatchEvent(
        new CustomEvent('whatsapp:call-reconnecting', { detail: { state } })
      );
    }
  };
  peer.addEventListener('iceconnectionstatechange', onIceState);
  onIceState();
};

export const applyOutboundAnswer = async (callId, sdpAnswer) => {
  // No in-flight peer connection in this tab → not our call.
  if (!pc) return;
  // /initiate hasn't returned our call id yet (the connect webhook raced
  // ahead). Buffer the answer; initiateOutboundCall flushes it by id once it
  // knows which call is ours, so a concurrent agent's answer is never applied.
  if (activeCallId == null) {
    pendingOutboundAnswers.set(callId, sdpAnswer);
    return;
  }
  // activeCallId known → only apply the answer for this tab's call.
  if (callId !== activeCallId) return;
  await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
  // Tracks are negotiated; the contact's phone is ringing.
  startWhatsappRingback();
  watchWhatsappMediaPath(pc);
};

// Called by the cable handler when Meta delivers status=ACCEPTED for the
// outbound call (real pickup). Flips the recorder gate and starts the
// MediaRecorder. Idempotent — safe if ontrack hasn't fired yet (setupRecorder
// bails until the remote stream has audio tracks; ontrack will retry).
export const armOutboundRecorder = () => {
  // Real pickup: the contact's phone stopped ringing.
  stopWhatsappRingback();
  recorderArmed = true;
  setupRecorder();
};

export const cleanupWhatsappSession = () => {
  whatsappMediaReady.value = false;
  whatsappReconnecting.value = false;
  stopWhatsappQualitySampling();
  cleanup();
};

export const handleWhatsappRemoteEnd = async callId => {
  // Snapshot before cleanup nulls activeCallId.
  const id = callId || activeCallId;
  if (!id) {
    cleanup();
    return;
  }
  await finalizeWhatsappSession(id);
};

export const setWhatsappCallMuted = muted => {
  const mic = bridgeMicStream || localStream;
  if (!mic) return false;
  mic.getAudioTracks().forEach(track => {
    track.enabled = !muted;
  });
  return muted;
};

export const sendWhatsappTerminateBeacon = () => {
  // Always fire when there's a live callId. The beacon endpoint is idempotent,
  // so racing it with an in-flight Axios terminate (which unload may abort) is
  // fine — Meta gets exactly one terminate either way, and we avoid leaving
  // the call ringing on Meta until its carrier-side timeout.
  if (!activeCallId) return;
  beaconTerminate(activeCallId);
};
